import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

// Button component
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 cursor-pointer rounded-lg disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:outline-none',
          {
            'bg-accent-primary text-white shadow-glow hover:-translate-y-0.5': variant === 'primary',
            'bg-background-tertiary text-foreground-secondary border border-border-subtle hover:border-border-hover': variant === 'secondary',
            'bg-transparent text-foreground-secondary hover:text-foreground-primary': variant === 'ghost',
          },
          {
            'px-3 py-1.5 text-sm': size === 'sm',
            'px-5 py-2.5 text-sm': size === 'md',
            'px-6 py-3 text-base': size === 'lg',
          },
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';

// Input component
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm text-foreground-muted mb-1.5">
            {label}
          </label>
        )}
        <input
          ref={ref}
          className={cn(
            'w-full px-4 py-3 bg-background-tertiary border border-border-subtle rounded-xl text-foreground-primary text-sm outline-none transition-colors focus:border-accent-primary',
            error && 'border-red-500',
            className
          )}
          {...props}
        />
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      </div>
    );
  }
);
Input.displayName = 'Input';

// Card component
interface CardProps {
  children: React.ReactNode;
  className?: string;
}

export function Card({ children, className }: CardProps) {
  return (
    <div
      className={cn(
        'bg-background-card border border-border-subtle rounded-2xl shadow-card',
        className
      )}
    >
      {children}
    </div>
  );
}

export function CardHeader({ children, className }: CardProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between px-5 py-4 border-b border-border-subtle',
        className
      )}
    >
      {children}
    </div>
  );
}

export function CardTitle({ children, className }: CardProps) {
  return (
    <h3 className={cn('text-sm font-semibold', className)}>{children}</h3>
  );
}

export function CardBody({ children, className }: CardProps) {
  return <div className={cn('p-5', className)}>{children}</div>;
}

// Badge component
interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'premium';
  className?: string;
}

export function Badge({ children, variant = 'default', className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold',
        {
          'bg-background-tertiary text-foreground-muted': variant === 'default',
          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400': variant === 'success',
          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400': variant === 'warning',
          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400': variant === 'danger',
          'bg-gradient-to-r from-accent-tertiary to-accent-primary text-white': variant === 'premium',
        },
        className
      )}
    >
      {children}
    </span>
  );
}

// Progress bar component
interface ProgressProps {
  value: number;
  max?: number;
  variant?: 'default' | 'success' | 'warning' | 'danger';
  className?: string;
}

export function Progress({ value, max = 100, variant = 'default', className }: ProgressProps) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  
  return (
    <div className={cn('h-1 bg-background-tertiary rounded-full overflow-hidden', className)}>
      <div
        className={cn('h-full rounded-full transition-all duration-300', {
          'bg-gradient-to-r from-accent-primary to-accent-secondary': variant === 'default',
          'bg-accent-secondary': variant === 'success',
          'bg-accent-tertiary': variant === 'warning',
          'bg-red-500': variant === 'danger',
        })}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

// Avatar component
interface AvatarProps {
  name: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
  };

  return (
    <div
      className={cn(
        'rounded-full bg-gradient-to-br from-accent-secondary to-accent-primary flex items-center justify-center font-semibold text-white',
        sizeClasses[size],
        className
      )}
    >
      {src ? (
        <img src={src} alt={name} className="w-full h-full rounded-full object-cover" />
      ) : (
        initials
      )}
    </div>
  );
}

// Icon button component
interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  tooltip?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, icon, tooltip, ...props }, ref) => {
    return (
      <button
        ref={ref}
        title={tooltip}
        aria-label={tooltip}
        className={cn(
          'w-9 h-9 rounded-lg bg-background-tertiary border border-border-subtle text-foreground-secondary flex items-center justify-center transition-all hover:bg-background-hover hover:text-foreground-primary focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:outline-none',
          className
        )}
        {...props}
      >
        {icon}
      </button>
    );
  }
);
IconButton.displayName = 'IconButton';

// Re-export Skeleton
export { Skeleton, DocumentSkeleton, StatSkeleton, CardSkeleton } from './Skeleton';

// Re-export Toast
export { ToastContainer, useToastStore, toast } from './Toast';
export type { Toast, ToastType } from './Toast';

// Re-export ConfirmModal
export { ConfirmModal, useConfirmModal } from './ConfirmModal';
export type { ConfirmModalProps } from './ConfirmModal';

// Re-export EditableTitle
export { EditableTitle } from './EditableTitle';
